<script setup lang="ts">
import type { Options as SplideOptions, Splide as SplideType } from '@splidejs/splide';
import { SplideSlide, Splide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';
import type { GalleryItem } from 'types';

const props = defineProps<{
  sliderOptions?: SplideOptions;
  content: GalleryItem[];
}>();

const slider = ref<InstanceType<typeof SplideType>>();
const thumbnails = ref<InstanceType<typeof SplideType>>();
const defaultSliderOptions = {
  type: 'slide',
  perPage: 1,
  perMove: 1,
  pagination: false,
  width: '100%',
  height: 'auto',
  heightRatio: 0.66,
  arrows: false,
  mediaQuery: 'min',
  speed: 400,
  breakpoints: {
    1024: {
      height: 344,
      width: 516,
    },
    1280: {
      height: 344,
      width: 516,
    },
    1536: {
      height: 445,
      width: 668,
    },
    1920: {
      height: 600,
      width: 900,
    },
  },
};

const thumbnailsOptions: SplideOptions = {
  type: 'slide',
  gap: '0.5rem',
  pagination: false,
  width: '100%',
  fixedWidth: 100,
  fixedHeight: 66,
  mediaQuery: 'min',
  arrows: false,
  cover: true,
  isNavigation: true,
  updateOnMove: true,
  direction: 'ltr',
  breakpoints: {
    1024: {
      direction: 'ltr',
      height: 344,
      width: 516,
    },
    1280: {
      direction: 'ttb',
      height: 344,
      fixedHeight: 100,
      fixedWidth: 150,
    },
    1536: {
      direction: 'ttb',
      height: 445,
    },
    1920: {
      direction: 'ttb',
      height: 600,
    },
  },
};

const computedSliderOptions: SplideOptions = computed(() => {
  return props.sliderOptions ?? defaultSliderOptions;
});

onMounted(() => {
  const thumbsSplide = thumbnails.value?.splide;
  if (thumbsSplide) {
    slider.value?.sync(thumbsSplide);
  }
});
</script>

<template>
  <section
    v-if="content"
    class="image-gallery relative flex flex-col-reverse gap-5 gap-x-2 xl:flex-row"
  >
    <Splide
      ref="thumbnails"
      class="image-gallery__thumbnails"
      :options="thumbnailsOptions"
    >
      <SplideSlide
        v-for="(item, index) in content"
        :id="`item-${index}`"
        :key="`item-${index}`"
        class="cursor-pointer list-none"
      >
        <BaseImage
          class="object-cover"
          :image="item.content.image"
          loading="eager"
          :modifiers="{
            mode: 'crop',
            center: `${item?.content.image.focalPoint?.left},${item?.content.image.focalPoint?.top}`,
          }"
          :width="thumbnailsOptions.fixedWidth"
          :height="thumbnailsOptions.fixedHeight"
          :preload="true"
        />
      </SplideSlide>
    </Splide>

    <Splide
      ref="slider"
      :disable-progress="true"
      class="relative"
      :options="computedSliderOptions"
    >
      <SplideSlide
        v-for="(slide, index) in content"
        :key="`slide-${index}`"
        class="flex justify-center"
      >
        <BaseImage
          class="size-full object-cover"
          :image="slide.content.image"
          loading="eager"
          :modifiers="{
            mode: 'crop',
            center: `${slide?.content.image.focalPoint?.left},${slide?.content.image.focalPoint?.top}`,
          }"
          :width="computedSliderOptions.width"
          :height="computedSliderOptions.height"
          :preload="true"
        />
      </SplideSlide>
    </Splide>
  </section>
</template>

<style scoped lang="postcss">
.image-gallery {
  &__thumbnails {
    .splide__slide.is-active {
      border: 3px solid var(--color-primary);
    }
  }
}
</style>
